import React, { Suspense } from 'react';
import { Alert, Box, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import FormWithStateContextProvider from 'contexts/FormWithStateContext';
import { useHistory, useParams } from 'react-router-dom';
import useSearchParams from 'hooks/ui/useSearchParams';

import usePartnerTransfersForm from './hooks/usePartnerTransfersForm';
import AddTransfer from './AddTransfer';
import { useLayoutContext } from 'contexts/LayoutContext';
import TransferActions from './Editor/TransferActions';
import Review from './Review';
import { PartnerTransferSection } from 'further-types/lp/partner-transfer';
import TransferLinkedCapitalActions from './Editor/TransferLinkedCapitalActions';
import api from 'lib/trpcClient';
import TransferGeneralCapitalActions from './Editor/TransferGeneralCapitalActions';
import TransferAdvanceSubscriptions from './Editor/TransferAdvanceSubscriptions';
import TransferFees from './Editor/TransferFees';
import SuccessPage from './SuccessPage';
import Page from 'components/new/Page';
import { PARTNER_TRANSFERS_LIST } from 'adminConstants/routes';
import PartnerTransferStepper from './PartnerTransferStepper';
import PartnerTransferActionBar from './PartnerTransferActionBar';

const LoadingContainer = styled(Box)({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const AddPartnerTransfer: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const { selectedVehicleId, vehicle } = useLayoutContext();
  const history = useHistory();
  const { searchParams, setSearchParam } = useSearchParams();

  const [sections] = api.partnerTransfers.getSections.useSuspenseQuery(
    { vehicleId: selectedVehicleId ?? '' },
    { staleTime: 0, gcTime: 0 },
  );

  const stepParam = searchParams.get('step');
  const parsedStep = stepParam !== null ? parseInt(stepParam, 10) : NaN;
  const activeStep =
    !isNaN(parsedStep) && parsedStep >= 0 && parsedStep <= sections.length
      ? !id && parsedStep > 0
        ? 0
        : parsedStep
      : 0;

  const setActiveStep = (step: number) => {
    setSearchParam('step', String(!id && step > 0 ? 0 : step));
  };

  // Load existing partner transfer data if editing
  const { data: existingPartnerTransfer } =
    api.partnerTransfers.getById.useQuery(
      {
        partnerTransferId: id ?? '',
        vehicleId: selectedVehicleId ?? '',
      },
      {
        enabled: !!id && !!selectedVehicleId,
        staleTime: 0,
        gcTime: 0,
      },
    );

  // Prepare default values from loaded data
  const formDefaultValues =
    !existingPartnerTransfer || !id
      ? undefined
      : {
          partnerTransferId: id,
          trasferrorCommitmentId:
            existingPartnerTransfer.transferorCommitmentId,
          transferDate: existingPartnerTransfer.transferDate,
          transferees: existingPartnerTransfer.newCommitments.transferees.map(
            (transferee) => ({
              investorLabel: transferee.investorName,
              investorId: transferee.investorId,
              amountLcy: transferee.amountLcy,
              percentage: transferee.percentage,
            }),
          ),
        };

  const { formMethods } = usePartnerTransfersForm({
    defaultValues: formDefaultValues,
  });

  const currentPartnerTransferId =
    id || formMethods.getValues('partnerTransferId') || '';

  const pageTitle = 'Partner transfer';

  const handleNext = (newPartnerTransferId?: string) => {
    const nextStep = activeStep + 1;
    if (newPartnerTransferId) {
      history.replace(
        `/partner-transfers/${newPartnerTransferId}?step=${nextStep}`,
      );
    } else {
      setActiveStep(nextStep);
    }
  };

  if (activeStep === sections.length) {
    return <SuccessPage />;
  }

  const currentSection = sections[activeStep];

  const initialState = {
    formMethods,
  };

  if (!vehicle) {
    return (
      <Page
        goBackAction={PARTNER_TRANSFERS_LIST}
        spacing="lg"
        withHeadingDivider
        heading={pageTitle}
      >
        <Alert severity="error">Vehicle not found.</Alert>
      </Page>
    );
  }

  const stepper = (
    <PartnerTransferStepper
      sections={sections}
      activeSection={currentSection}
    />
  );

  const pageFallback = (
    <Page
      goBackAction={PARTNER_TRANSFERS_LIST}
      spacing="lg"
      heading={pageTitle}
      withHeadingDivider
      customActions={stepper}
      actionBar={
        <PartnerTransferActionBar
          onNext={() => undefined}
          onPrevious={activeStep !== 0 ? () => undefined : undefined}
          isNextDisabled
          isPreviousDisabled
        />
      }
    >
      <LoadingContainer>
        <CircularProgress />
      </LoadingContainer>
    </Page>
  );

  return (
    <FormWithStateContextProvider
      formMethods={formMethods}
      initialState={initialState}
    >
      <Suspense fallback={pageFallback}>
        {currentSection === PartnerTransferSection.PartnerTransfer && (
          <AddTransfer
            onNext={handleNext}
            id={id}
            isSubmitting={formMethods.formState.isSubmitting}
            baseCurrency={vehicle.baseCurrency}
            vehicleId={vehicle._id}
            stepper={stepper}
          />
        )}
        {currentSection === PartnerTransferSection.Investments && (
          <TransferActions
            partnerTransferSection={PartnerTransferSection.Investments}
            partnerTransferId={currentPartnerTransferId}
            vehicleId={vehicle._id}
            baseCurrency={vehicle.baseCurrency}
            onPrevious={() => setActiveStep(activeStep - 1)}
            onNext={() => setActiveStep(activeStep + 1)}
            stepper={stepper}
          />
        )}
        {currentSection === PartnerTransferSection.Income && (
          <TransferActions
            partnerTransferSection={PartnerTransferSection.Income}
            partnerTransferId={currentPartnerTransferId}
            vehicleId={vehicle._id}
            baseCurrency={vehicle.baseCurrency}
            onPrevious={() => setActiveStep(activeStep - 1)}
            onNext={() => setActiveStep(activeStep + 1)}
            stepper={stepper}
          />
        )}
        {currentSection === PartnerTransferSection.Expenses && (
          <TransferActions
            partnerTransferSection={PartnerTransferSection.Expenses}
            partnerTransferId={currentPartnerTransferId}
            vehicleId={vehicle._id}
            baseCurrency={vehicle.baseCurrency}
            onPrevious={() => setActiveStep(activeStep - 1)}
            onNext={() => setActiveStep(activeStep + 1)}
            stepper={stepper}
          />
        )}
        {currentSection === PartnerTransferSection.Fees && (
          <TransferFees
            partnerTransferId={currentPartnerTransferId}
            baseCurrency={vehicle.baseCurrency}
            onPrevious={() => setActiveStep(activeStep - 1)}
            onNext={() => setActiveStep(activeStep + 1)}
            stepper={stepper}
          />
        )}
        {currentSection === PartnerTransferSection.LinkedCapitalActions && (
          <TransferLinkedCapitalActions
            partnerTransferId={currentPartnerTransferId}
            baseCurrency={vehicle.baseCurrency}
            onPrevious={() => setActiveStep(activeStep - 1)}
            onNext={() => setActiveStep(activeStep + 1)}
            stepper={stepper}
          />
        )}
        {currentSection === PartnerTransferSection.GeneralCapitalActions && (
          <TransferGeneralCapitalActions
            partnerTransferId={currentPartnerTransferId}
            baseCurrency={vehicle.baseCurrency}
            onPrevious={() => setActiveStep(activeStep - 1)}
            onNext={() => setActiveStep(activeStep + 1)}
            stepper={stepper}
          />
        )}
        {currentSection === PartnerTransferSection.AdvanceSubscriptions && (
          <TransferAdvanceSubscriptions
            partnerTransferId={currentPartnerTransferId}
            baseCurrency={vehicle.baseCurrency}
            onPrevious={() => setActiveStep(activeStep - 1)}
            onNext={() => setActiveStep(activeStep + 1)}
            stepper={stepper}
          />
        )}
        {currentSection === PartnerTransferSection.Summary && (
          <Review
            onPrevious={() => setActiveStep(activeStep - 1)}
            onApprove={() => setActiveStep(sections.length)}
            stepper={stepper}
          />
        )}
      </Suspense>
    </FormWithStateContextProvider>
  );
};

export default AddPartnerTransfer;
